import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

import {
  CaseTemplateFormValues,
  CaseTemplateWorkflowRuleFormValue,
  WorkflowRuleActionTaskWithId,
  WorkflowRuleTaskWithId,
} from "src/cases/templates/caseTemplateFormUtils";
import { useIdKeyMapping } from "src/cases/templates/useIdKeyMapping";

/**
 * Hook for managing workflow references (tasks and actions) with internal ID-based matching
 */
export const useWorkflowReferences = () => {
  const { getValues, setValue } = useFormContext<CaseTemplateFormValues>();
  const { isSameTask, isSameAction } = useIdKeyMapping();

  const removeTaskFromWorkflowTasks = useCallback(
    (
      statusKey: string,
      statusRules: CaseTemplateWorkflowRuleFormValue[string],
      taskToRemove: WorkflowRuleTaskWithId,
    ) => {
      if (statusRules.workflow_tasks_keys?.length) {
        const updated = statusRules.workflow_tasks_keys.filter(
          (task) => !isSameTask(task, taskToRemove),
        );
        if (updated.length !== statusRules.workflow_tasks_keys.length) {
          setValue(
            `attributes.workflow_rules.${statusKey}.workflow_tasks_keys`,
            updated,
            { shouldDirty: true, shouldValidate: true },
          );
        }
      }
    },
    [setValue, isSameTask],
  );

  const removeTaskFromActionRequiredTasks = useCallback(
    (
      statusKey: string,
      actionIndex: number,
      requiredTasks: WorkflowRuleActionTaskWithId[],
      taskToRemove: WorkflowRuleTaskWithId,
    ) => {
      const updated = requiredTasks.filter(
        (task) => !isSameTask(task, taskToRemove),
      );
      if (updated.length !== requiredTasks.length) {
        setValue(
          `attributes.workflow_rules.${statusKey}.actions.${actionIndex}.required_tasks_keys`,
          updated,
          { shouldDirty: true, shouldValidate: true },
        );
      }
    },
    [setValue, isSameTask],
  );

  const updateTaskKeyInRequiredTasks = useCallback(
    (
      statusKey: string,
      actionIndex: number,
      requiredTasks: WorkflowRuleActionTaskWithId[],
      internalId: string,
      newKey: string,
    ) => {
      const updated = requiredTasks.map((task) =>
        task._internalId === internalId ? { ...task, task_key: newKey } : task,
      );
      setValue(
        `attributes.workflow_rules.${statusKey}.actions.${actionIndex}.required_tasks_keys`,
        updated,
        { shouldDirty: true, shouldValidate: true },
      );
    },
    [setValue],
  );

  const removeTaskFromAllWorkflowRules = useCallback(
    (internalId: string) => {
      if (!internalId) return;

      const workflowRules = getValues("attributes.workflow_rules");
      if (!workflowRules) return;

      const taskToRemove = {
        task_key: "",
        order: 0,
        _internalId: internalId,
      };

      Object.keys(workflowRules).forEach((statusKey) => {
        const statusRules = workflowRules[statusKey];
        if (!statusRules) return;

        removeTaskFromWorkflowTasks(statusKey, statusRules, taskToRemove);

        if (statusRules.actions?.length) {
          statusRules.actions.forEach((action, actionIndex) => {
            const requiredTasks = action.required_tasks_keys as
              | WorkflowRuleActionTaskWithId[]
              | undefined;
            if (requiredTasks?.length) {
              removeTaskFromActionRequiredTasks(
                statusKey,
                actionIndex,
                requiredTasks,
                taskToRemove,
              );
            }
          });
        }
      });
    },
    [getValues, removeTaskFromWorkflowTasks, removeTaskFromActionRequiredTasks],
  );

  const removeAction = useCallback(
    (internalId: string) => {
      if (!internalId) return;

      const workflowRules = getValues("attributes.workflow_rules");
      if (!workflowRules) return;

      const actionToRemove = {
        action_key: "",
        automation: [],
        _internalId: internalId,
      };

      Object.keys(workflowRules).forEach((statusKey) => {
        const statusRules = workflowRules[statusKey];
        if (!statusRules?.actions?.length) return;

        const updated = statusRules.actions.filter(
          (action) => !isSameAction(action, actionToRemove),
        );
        if (updated.length !== statusRules.actions.length) {
          setValue(`attributes.workflow_rules.${statusKey}.actions`, updated, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      });
    },
    [getValues, setValue, isSameAction],
  );

  const updateTaskKey = useCallback(
    (internalId: string, newKey: string) => {
      if (!internalId || !newKey) return;

      const workflowRules = getValues("attributes.workflow_rules");
      if (!workflowRules) return;

      Object.keys(workflowRules).forEach((statusKey) => {
        const statusRules = workflowRules[statusKey];
        if (!statusRules) return;

        if (statusRules.workflow_tasks_keys?.length) {
          const updated = statusRules.workflow_tasks_keys.map((task) =>
            task._internalId === internalId
              ? { ...task, task_key: newKey }
              : task,
          );
          setValue(
            `attributes.workflow_rules.${statusKey}.workflow_tasks_keys`,
            updated,
            { shouldDirty: true, shouldValidate: true },
          );
        }

        if (statusRules.actions?.length) {
          statusRules.actions.forEach((action, actionIndex) => {
            const requiredTasks = action.required_tasks_keys as
              | WorkflowRuleActionTaskWithId[]
              | undefined;
            if (requiredTasks?.length) {
              updateTaskKeyInRequiredTasks(
                statusKey,
                actionIndex,
                requiredTasks,
                internalId,
                newKey,
              );
            }
          });
        }
      });
    },
    [getValues, setValue, updateTaskKeyInRequiredTasks],
  );

  const updateActionKey = useCallback(
    (internalId: string, newKey: string) => {
      if (!internalId || !newKey) return;

      const workflowRules = getValues("attributes.workflow_rules");
      if (!workflowRules) return;

      Object.keys(workflowRules).forEach((statusKey) => {
        const statusRules = workflowRules[statusKey];
        if (!statusRules?.actions?.length) return;

        const updated = statusRules.actions.map((action) =>
          action._internalId === internalId
            ? { ...action, action_key: newKey }
            : action,
        );
        setValue(`attributes.workflow_rules.${statusKey}.actions`, updated, {
          shouldDirty: true,
          shouldValidate: true,
        });
      });
    },
    [getValues, setValue],
  );

  const removeTaskFromSpecificStatus = useCallback(
    (internalId: string, statusKey: string) => {
      if (!internalId) return;

      const statusRules = getValues(`attributes.workflow_rules.${statusKey}`);
      if (!statusRules?.actions?.length) return;

      const taskToRemove = {
        task_key: "",
        _internalId: internalId,
      };

      statusRules.actions.forEach((action, actionIndex) => {
        const requiredTasks = action.required_tasks_keys as
          | WorkflowRuleActionTaskWithId[]
          | undefined;
        if (requiredTasks?.length) {
          removeTaskFromActionRequiredTasks(
            statusKey,
            actionIndex,
            requiredTasks,
            taskToRemove,
          );
        }
      });
    },
    [getValues, removeTaskFromActionRequiredTasks],
  );

  return {
    removeTaskFromAllWorkflowRules,
    removeAction,
    updateTaskKey,
    updateActionKey,
    removeTaskFromSpecificStatus,
  };
};
